import {
  ActivityTimeline,
  type ActivityTimelineEvent,
} from "@/components/public-tracking/ActivityTimeline";
import { formatScanTimestamp } from "@/utils/publicTracking/format";
import { STATUS_PALETTE } from "@/utils/publicTracking/statusColors";
import {
  formatTrackingLocation,
  trackingEventStatusLabel,
} from "@/utils/publicTracking/trackingStatus";
import type { PublicTrackingEvent } from "@/utils/publicTracking/types";

interface ScanTimelineProps {
  events: PublicTrackingEvent[];
}

/** Scan-history list — hosted inside the shared two-column detail card. */
export function ScanTimeline({ events }: ScanTimelineProps) {
  // API is oldest-first, so the last element is the newest scan. ActivityTimeline
  // re-sorts newest-first for display; we only need to flag the latest event.
  const timelineEvents: ActivityTimelineEvent[] = events.map((event, i) => {
    const isLatest = i === events.length - 1;
    return {
      id: `${event.timestamp}-${i}`,
      title: trackingEventStatusLabel(event.status),
      actor: formatTrackingLocation(event.location),
      timestamp: event.timestamp,
      category: isLatest ? "latest" : "past",
      badgeLabel: isLatest ? "Latest" : "",
      highlighted: isLatest,
      dotClassName: isLatest
        ? "border-orange-500 bg-orange-500"
        : "border-emerald-600 bg-emerald-600",
    };
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Scan History</h2>
        <span className="text-muted-foreground text-xs">
          {events.length} {events.length === 1 ? "event" : "events"}
        </span>
      </div>

      {events.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Accepted &mdash; not yet scanned. USPS scan events typically appear
          within a few hours of mailing.
        </p>
      ) : (
        <ActivityTimeline
          events={timelineEvents}
          colorMap={{
            latest: "bg-orange-500/10 text-orange-700 uppercase tracking-wide",
            past: STATUS_PALETTE.neutral,
          }}
          formatTimestamp={formatScanTimestamp}
        />
      )}
    </div>
  );
}
